import React, { useEffect } from 'react';
import { X, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Service, Language } from '../types';

interface ServiceDetailProps {
  service: Service;
  onClose: () => void;
  lang: Language;
}

export const ServiceDetail: React.FC<ServiceDetailProps> = ({ service, onClose, lang }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const isRTL = lang === 'ar';

  return (
    <div className="fixed inset-0 z-[60] bg-luxury-paper overflow-y-auto animate-fade-in" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Close Button */}
      <button 
        onClick={onClose}
        className="fixed top-8 right-8 z-50 p-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white hover:bg-white hover:text-black transition-all duration-300 group shadow-lg"
      >
        <X size={24} strokeWidth={1} className="group-hover:rotate-90 transition-transform duration-300" />
      </button>

      {/* Hero Image */}
      <div className="relative h-[60vh] w-full overflow-hidden">
        <img 
          src={service.image} 
          alt={service.title}
          className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-[20s] ease-in-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-luxury-black via-luxury-black/40 to-transparent"></div>
        
        <div className="absolute bottom-0 left-0 w-full p-8 md:p-16 max-w-[1920px] mx-auto">
          <div className="animate-fade-up">
            <div className="flex items-center gap-4 mb-6">
               <div className="h-px w-12 bg-greenline-400"></div>
               <span className="text-greenline-400 text-xs uppercase tracking-[0.3em] font-medium">
                  {lang === 'ar' ? 'تفاصيل الخدمة' : 'Service Details'}
               </span>
            </div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-white mb-6 leading-[0.9]">
              {service.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1920px] mx-auto px-6 md:px-16 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          
          {/* Main Description */}
          <div className="lg:col-span-7 animate-fade-up" style={{ animationDelay: '0.2s' }}>
            <h2 className="font-serif text-3xl md:text-4xl text-luxury-black mb-8 italic">
              {lang === 'ar' ? 'نظرة عامة' : 'Overview'}
            </h2>
            <p className="text-xl leading-relaxed text-stone-600 font-light text-balance whitespace-pre-line">
              {service.details}
            </p>
          </div>

          {/* Sidebar / Features */}
          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: '0.4s' }}>
            <div className="bg-white p-10 border border-stone-100 shadow-sm sticky top-8">
               <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-luxury-black mb-8 border-b border-stone-100 pb-4">
                  {lang === 'ar' ? 'المميزات الرئيسية' : 'Key Features'}
               </h3>
               <ul className="space-y-6 mb-10">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-4 group">
                      <CheckCircle2 className="text-greenline-500 mt-1 shrink-0 group-hover:scale-110 transition-transform" size={18} strokeWidth={1.5} />
                      <span className="text-stone-600 font-light group-hover:text-luxury-black transition-colors">{feature}</span>
                    </li>
                  ))}
               </ul>
               
               <a href="#contact" onClick={onClose} className="flex items-center justify-between w-full bg-luxury-black text-white p-6 hover:bg-greenline-900 transition-colors group">
                  <span className="text-xs uppercase tracking-[0.2em]">
                    {lang === 'ar' ? 'طلب هذه الخدمة' : 'Request Service'}
                  </span>
                  <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform rtl:group-hover:-translate-x-2" />
               </a>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};